import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, CheckCircle, UserPlus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import SignatureCapture from '../components/shared/SignatureCapture';
import FormPhotoUpload from '../components/shared/FormPhotoUpload';
import DatePicker from '../components/DatePicker';
import { useFormSubmission } from '../hooks/useFormSubmission';
import { api } from '../api/client';
import { todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useAuth } from '../contexts/AuthContext';
import { useHideAgentFab } from '../components/AgentFab';
import { getUserDisplayName } from '../lib/user-display';
import { unwrapArray } from '../lib/api-envelope';
import { useMyWork } from '../hooks/useMyWork';
import { linkableItems, workItemLabel, workItemValue, workItemBodyFields, isWorkItemValue } from '../lib/work-item-selection';

interface Attendee {
  id: string;
  userId?: string;
  name: string;
  signatureKey: string;
}

/** Sentinel for "no specific job", so it is distinguishable from "unpicked". */
const GENERAL_MEETING = '__general__';

interface UploadedPhoto {
  key: string;
  url: string;
  label?: string;
}

export default function PreJobMeetingForm() {
  useHideAgentFab();  // bottom action bar — the FAB would cover Submit.
  const navigate = useNavigate();
  const { user, hasVertical } = useAuth();
  // Positive guards, one per vertical. GET /jobs is casing-gated.
  const hasCasing = hasVertical('casing_running');
  const hasPt = hasVertical('pressure_testing');
  const timeZone = useTenantTimeZone();

  // Load team members for attendee picker
  const { data: usersData } = useQuery({
    queryKey: ['users-for-prejob-meeting'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const users: any[] = usersData ?? [];

  /* Jobs for the (optional) job link. `safety_meetings.job_id` is nullable and
   * a general meeting is a real thing, so this must never become required.
   *
   * Filtered exactly like the JSA picker, and for the same reason: a pre-job
   * meeting is held BEFORE the work, so a completed or cancelled job is never
   * the right answer. /jobs returns every status — on Smith Casing that is
   * half the rows — and burying the schedulable jobs among them is how the
   * wrong job gets picked. Client-side on purpose; the endpoint is shared with
   * screens that DO want the full history.
   *
   * Report Incident deliberately does the opposite. That form records
   * something that already happened, possibly on a job that has since
   * completed. The three pickers differ because the forms point in different
   * directions in time — not an oversight to reconcile. */
  const { data: jobsData } = useQuery({
    queryKey: ['jobs-for-prejob-meeting'],
    queryFn: () => api.jobs.list().then(r => unwrapArray<any>(r.data)),
    enabled: hasCasing,
  });
  // The hand's own open PT jobs (GET /field/my-work) — open only, for the same
  // before-the-work reason as the casing filter below.
  const { data: myWork } = useMyWork('open', { enabled: hasPt });
  const ptItems = linkableItems(myWork?.items, 'pressure_testing');
  const jobs: any[] = (jobsData ?? []).filter((j: any) => {
    const status = (j.status || j.job_status || '').toLowerCase();
    return status !== 'completed' && status !== 'cancelled' && status !== 'canceled';
  });

  const userName = (u: any) => getUserDisplayName(u);

  const submissionId = useMemo(() => uuidv4(), []);

  /* `/auth/me` answers { fullName } — no first_name/last_name and no
   * firstName/lastName — so the old first/last lookup always came up empty and
   * fell through to `user.email`. That is how "admin@smithcasing.com" ended up
   * stored as a Meeting Leader. Checking both CASINGS would not have fixed it;
   * the resolver has to know about fullName, which is why it lives in one
   * place now. */
  const defaultLeader = getUserDisplayName(user);

  const [topic, setTopic] = useState('');
  const [jobId, setJobId] = useState('');
  const [location, setLocation] = useState('');
  const [meetingDate, setMeetingDate] = useState(() => todayCalendarDate(timeZone) ?? '');
  const [leaderName, setLeaderName] = useState(defaultLeader);
  const [leaderTouched, setLeaderTouched] = useState(false);

  /* `user` is null on the first render while /auth/me is in flight, so seeding
   * useState alone leaves the field permanently blank for anyone who lands
   * here on a cold load. Fill it in when the profile arrives — but only while
   * the field is still untouched, so this never overwrites a typed name. */
  useEffect(() => {
    if (leaderTouched || !defaultLeader) return;
    setLeaderName((prev) => (prev ? prev : defaultLeader));
  }, [defaultLeader, leaderTouched]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [hazards, setHazards] = useState('');
  const [jsaAcknowledged, setJsaAcknowledged] = useState(false);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [leaderSignatureKey, setLeaderSignatureKey] = useState('');
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Capture GPS silently on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}, // silently ignore errors
        { enableHighAccuracy: false, timeout: 10000 },
      );
    }
  }, []);

  const { submit, isSubmitting, error } = useFormSubmission<any>({
    endpoint: '/forms/pre-job-meeting',
    onSuccess: () => {
      setSuccessMessage('Pre-Job Meeting submitted successfully.');
      setTimeout(() => navigate('/my-work/forms'), 1500);
    },
  });

  const hasAttendees = attendees.some((a) => a.name.trim() !== '');

  const isValid =
    topic.trim() !== '' &&
    meetingDate !== '' &&
    leaderName.trim() !== '' &&
    jsaAcknowledged &&
    leaderSignatureKey !== '' &&
    hasAttendees;

  const availableUsers = users.filter(
    (u) => u.role !== 'super_admin' && !attendees.some((a) => a.userId === u.id),
  );

  const addTeamAttendee = (userId: string) => {
    if (!userId) return;
    const u = users.find((x: any) => x.id === userId);
    if (!u) return;
    setAttendees((prev) => [...prev, { id: uuidv4(), userId: u.id, name: userName(u), signatureKey: '' }]);
  };

  const addExternalAttendee = () => {
    setAttendees((prev) => [...prev, { id: uuidv4(), name: '', signatureKey: '' }]);
  };

  const removeAttendee = (id: string) => {
    setAttendees((prev) => prev.filter((a) => a.id !== id));
  };

  const updateAttendeeName = (id: string, name: string) => {
    setAttendees((prev) => prev.map((a) => (a.id === id ? { ...a, name } : a)));
  };

  const updateAttendeeSignature = (id: string, key: string) => {
    setAttendees((prev) => prev.map((a) => (a.id === id ? { ...a, signatureKey: key } : a)));
  };

  /**
   * The leader as a user id, when the typed name resolves to a known person.
   *
   * Sent alongside the name so future rows resolve by id instead of by string
   * match — a renamed or re-spelled person currently breaks the join. Left
   * undefined for an external leader or an ambiguous name (two people sharing
   * a display name resolve to neither, rather than to whichever sorted first).
   */
  const resolveLeaderUserId = (): string | undefined => {
    const typed = leaderName.trim().toLowerCase();
    if (!typed) return undefined;
    if (user && getUserDisplayName(user).toLowerCase() === typed) return user.id;
    const matches = users.filter((u: any) => userName(u).toLowerCase() === typed);
    return matches.length === 1 ? matches[0].id : undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    await submit({
      meetingTopic: topic.trim(),
      /* Omitted, not null, for a general meeting. Every other optional field on
       * this payload drops its key the same way, and JSON.stringify drops
       * `undefined` — so the column keeps its NULL without the endpoint having
       * to accept one. `__general__` is the sentinel a native <select> needs to
       * tell "deliberately no job" apart from "nothing picked yet"; both land
       * here as absent, which is the same record either way. */
      jobId: jobId && jobId !== GENERAL_MEETING && !isWorkItemValue(jobId) ? jobId : undefined,
      // A PT job is a work item, never a jobId (api#976). Adds no key for a
      // casing or general meeting, so their body is unchanged.
      ...workItemBodyFields(jobId),
      meetingLocation: location.trim() || undefined,
      meetingDate,
      meetingLeaderName: leaderName.trim(),
      meetingLeaderUserId: resolveLeaderUserId(),
      attendees: attendees
        .filter((a) => a.name.trim())
        .map((a) => ({ name: a.name.trim(), signatureR2Key: a.signatureKey || undefined })),
      hazardsDiscussed: hazards.trim() || undefined,
      jsaAcknowledged,
      notes: notes.trim() || undefined,
      photoR2Keys: photos.map((p) => p.key).filter((k) => !k.startsWith('pending-')),
      signatureR2Key: leaderSignatureKey || undefined,
      gpsLat: gps?.lat,
      gpsLng: gps?.lng,
    });
  };

  // Success screen
  if (successMessage) {
    return (
      <div className="min-h-full bg-background flex flex-col items-center justify-center px-6 py-12">
        <CheckCircle size={56} className="text-green-400 mb-4" />
        <h2 className="text-xl font-bold text-white mb-1">Submitted</h2>
        <p className="text-gray-400 text-sm">{successMessage}</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
          Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Pre-Job Meeting</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 pb-32">
        {/* Meeting Topic */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <label className="block text-sm text-fieldiq-text mb-1">
            Meeting Topic <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Daily safety briefing"
            className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
          />
        </div>

        {/* Job + Location + Date */}
        <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">
              Job <span className="text-fieldiq-muted">(optional)</span>
            </label>
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
            >
              <option value="">— Select an option —</option>
              <option value={GENERAL_MEETING}>General meeting (no specific job)</option>
              {jobs.map((j: any) => (
                <option key={j.id} value={j.id}>
                  {j.job_number || j.jobNumber} - {j.customer_name || j.customerName || j.well_name || 'Untitled'}
                </option>
              ))}
              {ptItems.map((item) => (
                <option key={workItemValue(item)} value={workItemValue(item)}>
                  {workItemLabel(item)}
                </option>
              ))}
            </select>
            {hasPt && myWork && ptItems.length === 0 && jobs.length === 0 && (
              <p className="text-fieldiq-muted text-xs mt-1" data-testid="prejob-no-work">
                You have no open pressure-testing jobs. Choose General meeting to file one without a job.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Meeting Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Well site, shop yard"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">
                Date <span className="text-red-400">*</span>
              </label>
              <DatePicker
                value={meetingDate}
                onChange={(v) => setMeetingDate(v)}
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">
                Meeting Leader <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={leaderName}
                onChange={(e) => { setLeaderTouched(true); setLeaderName(e.target.value); }}
                placeholder="Leader name"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
              />
            </div>
          </div>
        </div>

        {/* Attendees */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white">
              Attendees <span className="text-red-400">*</span>
            </h3>
            <div className="flex gap-2">
              {availableUsers.length > 0 && (
                <select
                  value=""
                  onChange={(e) => { addTeamAttendee(e.target.value); e.target.value = ''; }}
                  className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-amber-400"
                >
                  <option value="">+ Team Member</option>
                  {availableUsers.map((u: any) => (
                    <option key={u.id} value={u.id}>{userName(u)}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={addExternalAttendee}
                className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 transition-colors"
              >
                <UserPlus size={16} /> External
              </button>
            </div>
          </div>

          {attendees.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No attendees added yet. Add team members or external attendees. At least one is required.</p>
          ) : (
            <div className="space-y-4">
              {attendees.map((att, idx) => (
                <div key={att.id} className="bg-fieldiq-dark rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-fieldiq-muted font-mono w-5">{idx + 1}.</span>
                    {att.userId ? (
                      <div className="flex-1 px-3 py-2 bg-fieldiq-card rounded-lg text-white text-sm">
                        {att.name}
                        <span className="text-fieldiq-muted text-xs ml-2">(team)</span>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={att.name}
                        onChange={(e) => updateAttendeeName(att.id, e.target.value)}
                        placeholder="External attendee name"
                        className="flex-1 bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttendee(att.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {att.name.trim() && (
                    <SignatureCapture
                      formName="pre_job_meeting"
                      submissionId={submissionId}
                      label={`${att.name}'s signature`}
                      onSigned={(key) => updateAttendeeSignature(att.id, key)}
                      onCleared={() => updateAttendeeSignature(att.id, '')}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Hazards */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <label className="block text-sm text-fieldiq-text mb-1">Hazards Discussed</label>
          <textarea
            rows={3}
            value={hazards}
            onChange={(e) => setHazards(e.target.value)}
            placeholder="List any hazards identified and discussed..."
            className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
          />
        </div>

        {/* JSA Acknowledgement */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={jsaAcknowledged}
              onChange={(e) => setJsaAcknowledged(e.target.checked)}
              className="w-5 h-5 rounded mt-0.5 accent-amber-500"
            />
            <div>
              <span className="text-white font-medium">
                JSA Acknowledged <span className="text-red-400">*</span>
              </span>
              <p className="text-xs text-fieldiq-muted mt-0.5">
                All attendees have reviewed and acknowledged the Job Safety Analysis for this work scope.
              </p>
            </div>
          </label>
        </div>

        {/* Notes */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <label className="block text-sm text-fieldiq-text mb-1">Notes</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white"
          />
        </div>

        {/* Photos */}
        <FormPhotoUpload
          submissionId={submissionId}
          onChange={setPhotos}
          value={photos}
          maxPhotos={6}
          label="Photos"
        />

        {/* Leader Signature */}
        <SignatureCapture
          formName="pre_job_meeting"
          submissionId={submissionId}
          label="Meeting Leader Signature"
          onSigned={(key) => setLeaderSignatureKey(key)}
          onCleared={() => setLeaderSignatureKey('')}
        />

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
            <p className="text-red-400 text-sm">{error.message || 'Submission failed. Please try again.'}</p>
          </div>
        )}

        {/* Submit */}
        <div className="pb-4">
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`w-full py-3 rounded-lg font-semibold text-base transition-colors ${
              isValid && !isSubmitting
                ? 'bg-amber-500 text-fieldiq-dark hover:bg-amber-400'
                : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Pre-Job Meeting'}
          </button>
        </div>
      </form>
    </div>
  );
}
